// @flow

import * as React from 'react';

import { CornerDownRight } from 'lucide-react';

import { selectCartIsActive, ProductIcon, type ProductType } from '@gandi/cart';
import { Base } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import { ListRow } from '../../../../ListRow';

import ConfirmationStep from './ConfirmationStep';
import Info from './Info';
import Label from './Label';
import RemoveButton from './RemoveButton';

import styles from './TitleRow.scss';

export type Props = {
  /** main line shown in bold */
  label: React.Node,
  /** @deprecated second line; to replace with a  new `ListRow` component in each ProductKind component for NDS. */
  detail?: React.Node,
  /** @deprecated top right info, ideal for expiration dates; will go in the same `ListRow` as `detail`. */
  info?: string,

  /** add Corporate  badge */
  isCorporate?: boolean,

  /** do not allow delete of this product */
  deleteDisabled?: boolean,
  /** action to remove the product */
  handleRemoveAction?: (SyntheticEvent<HTMLButtonElement>) => mixed,
  /** add a warning to the delete confirmation */
  warningContent?: React.Node,

  /** set the line as busy */
  working?: boolean,

  /* Props under FF_CART_OVERVIEW_NDS */

  /** icon depending on the product type */
  productType?: ProductType,
};

export default function TitleRow({
  deleteDisabled,
  label,
  detail,
  isCorporate,
  handleRemoveAction,
  info,
  working = false,
  warningContent,
  productType,
}: Props): React.MixedElement {
  // TODO: try to use product.isWorking instead
  const active = useAppSelector(selectCartIsActive);

  const [confirmationStep, setConfirmationStep] = React.useState(false);

  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();

  return (
    <TitleRowContainer detail={detail} info={info}>
      <Base flex flexGrow1 flexWrap flexJustifyBetween gap="xsmall">
        <Label isCorporate={isCorporate}>
          {FF_CART_OVERVIEW_NDS && (
            <ProductIcon type={productType} className={styles.icon} />
          )}
          {label}
        </Label>

        {!FF_CART_OVERVIEW_NDS && !!info && !confirmationStep && (
          <Info>{info}</Info>
        )}

        {confirmationStep && handleRemoveAction && (
          <ConfirmationStep
            fireConfirmationStep={setConfirmationStep}
            handleRemoveAction={handleRemoveAction}
            warningContent={warningContent}
          />
        )}
      </Base>
      {handleRemoveAction &&
        !deleteDisabled &&
        active &&
        !working &&
        !confirmationStep && (
          <RemoveButton fireConfirmationStep={setConfirmationStep} />
        )}
    </TitleRowContainer>
  );
}

function TitleRowContainer({
  children,
  detail,
  info,
}: {
  children: React.ChildrenArray<React.Node>,
  /** second line */
  detail?: React.Node,
  /**
   * top-right info, ideal for expiration dates
   * @deprecated will be removed in https://gitlab.corp.gandi.net/gandi/squad-shop/app-shop/-/issues/315
   * and moved to each Product component in the new NDS style
   */
  info?: string,
}): React.MixedElement {
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();

  if (FF_CART_OVERVIEW_NDS) {
    return (
      <>
        <Base className={styles.root}>
          <Base
            flex
            flexAlignItemsCenter
            flexJustifyBetween
            size="big"
            className={styles.main}
          >
            {children}
          </Base>
        </Base>

        {/* TODO NDS: move this in each Product component itself in #215 */}
        {!!detail && (
          <ListRow
            intent="muted"
            icon={<CornerDownRight size={16} />}
            title={detail}
            options={info ? [info] : undefined}
            optionsArrow={false}
          />
        )}
      </>
    );
  }

  /* Legacy style */
  return (
    <Base
      paddingTop="xsmall"
      paddingBottom="small"
      borderedTop
      className={styles.legacyRoot}
    >
      <Base flex flexAlignItemsBaseline flexJustifyBetween gap="small">
        {children}
      </Base>
      {!!detail && <Base color="light">{detail}</Base>}
    </Base>
  );
}
